/**
 * "What matters most to John and Kerry?" — the four goals as one control (UX review, 17 Oct 2026).
 *
 * The same four goals the owner ruled on (24 Sep 2026), in the same order, with a plain sub-line under
 * each so a family member can pick one without knowing what "optimised for" means. Presentation only:
 * choosing a goal never re-runs the engine, it chooses which of the ranked rows to show.
 *
 * The "…and income covers the costs" goal is engine logic, not copy: for a self-funded client it picks a
 * different plan. When no plan meets it, as for most families, it is shown dimmed with "Same as 'Leave
 * the most' for John" rather than hidden (owner, 17 Oct 2026: clients almost always dip into savings).
 */
import React from 'react';

export type GoalFocusId = 'legacy' | 'covered' | 'cashflow' | 'pension';

interface EngineGoalControlProps {
    value: GoalFocusId;
    onChange: (id: GoalFocusId) => void;
    /** Who the plans are for — "John and Kerry", "John". */
    household: string;
    /** The person entering care, for the dimmed sub-line. */
    enteringName: string;
    /** How many years the estate figure looks ahead. */
    horizonYears: number;
    /** True when the cashflow-covered leader is the same plan as "Leave the most" (dimmed, still choosable). */
    coveredSameAsBest: boolean;
    /** How many ways were tested, for the line under the question. */
    waysTested: number;
    /** How many plans the current goal shows. */
    shown: number;
    /** "to pay for the room" — what the ways were tested for. */
    testedFor?: string;
    /** The closing sentence; aged care's is about savings running out before care ends. */
    closingNote?: React.ReactNode;
    /** Per-goal sub-lines in place of the defaults (e.g. "Same plan as 'leave the most'"). */
    subs?: Partial<Record<GoalFocusId, string>>;
    /**
     * When the two goals share a plan BECAUSE the plan that leaves the most also covers each year from
     * income (19 Oct 2026, caught live on Colin and Shelly: the line said no plan covers the costs over a
     * card with $9,604 a year to spare). Absent or false keeps the "no plan covers" reading.
     */
    bestCovers?: boolean;
    /** In place of the "Two fit this goal best." sentence (the village: "No way of moving keeps any Age Pension."). */
    countLine?: React.ReactNode;
    /** What the plans are compared with, in this module's words ("paying daily", "the support alone"). */
    baselineWord?: string;
}

const EngineGoalControl = ({ value, onChange, household, enteringName, horizonYears, coveredSameAsBest, waysTested, shown, testedFor = 'to pay for the room', closingNote, subs, bestCovers = false, countLine, baselineWord = 'paying daily' }: EngineGoalControlProps) => {
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    const options: Array<{ id: GoalFocusId; label: string; sub: string; dim?: boolean }> = [
        { id: 'legacy', label: 'Leave the most to the family', sub: subs?.legacy ?? `Biggest estate after about ${yrs}` },
        { id: 'covered', label: '…and income covers the costs', sub: subs?.covered ?? (coveredSameAsBest ? `Same as "Leave the most" for ${enteringName}` : 'Pays its way without savings'), dim: coveredSameAsBest },
        { id: 'cashflow', label: 'Most left over each year', sub: subs?.cashflow ?? 'Easiest cashflow' },
        { id: 'pension', label: 'Highest Age Pension', sub: subs?.pension ?? 'Most government support' },
    ];
    return (
        <div data-testid="engine-goal-control">
            <h2 className="text-[22px] font-bold leading-tight tracking-tight text-slate-900">What matters most to {household}?</h2>
            <p className="mt-1 text-[14px] text-slate-600">
                We tested <strong className="text-slate-800">{waysTested.toLocaleString()} ways</strong> {testedFor}.{' '}
                {countLine ?? (shown === 1 ? 'One fits this goal best.' : shown === 0 ? `None beats ${baselineWord} on this goal.` : `${shown === 2 ? 'Two' : shown} fit this goal best.`)}
                {coveredSameAsBest && bestCovers && <> The plan that leaves the most also covers each year&apos;s costs from income, so &quot;income covers the costs&quot; gives the same plan here.</>}
                {coveredSameAsBest && !bestCovers && <> No plan covers each year&apos;s costs from income alone — most families use some savings — so &quot;income covers the costs&quot; gives the same plan as &quot;Leave the most&quot; here.</>}
                {' '}{closingNote ?? <>Each card says when the savings would run out; many people&apos;s care ends well before that day.</>}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-1.5 rounded-xl border border-slate-300 bg-white p-1.5 shadow-card sm:grid-cols-2 lg:grid-cols-4" role="radiogroup" aria-label="What matters most">
                {options.map((o) => {
                    const on = value === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => onChange(o.id)}
                            className={`rounded-lg px-4 py-3 text-left transition-colors ${on ? 'bg-brand-navy text-white' : 'text-slate-700 hover:bg-slate-50'} ${o.dim && !on ? 'opacity-60' : ''}`}
                        >
                            <span className="block text-[14px] font-bold leading-snug">{o.label}</span>
                            <span className={`block text-[12px] leading-snug ${on ? 'text-blue-100' : 'text-slate-500'}`}>{o.sub}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default EngineGoalControl;
